import React, { ReactElement, useEffect, useMemo, useState, VFC } from 'react';
import { usePostHog } from 'posthog-js/react'

import { SubscriptionPaymentIntervalTypes, SubscriptionPlanObject, SubscriptionTypes } from '..';
import { DashboardSettingsBillingFeatureMapping } from '../feature-mapping';
import { DashboardSettingsBillingSubscriptionCard } from './card';
import { DashboardSettingsBillingSubscriptionCurrentPlan } from './current-plan';
import classNames from 'classnames';
import { Nullable } from 'src/types/nullable.type';
import { BillingToggle } from './billing-toggle';
import { useCurrentUser } from 'src/store/user';
import { useLocation } from 'react-router-dom';
import TrialStatus from './TrialStatus';
import { UpgradeConfirmationModal } from './upgrade-confirmation-modal';
import { apiSubscribeUpgrade } from 'src/utils/journeyApi';
import { useCurrentOrganization } from 'src/store/organization';
import { useNotificationMessage } from 'src/common/notification/message.hook';

const ContactUsCalendly = 'https://calendly.com/journey-danny/30min?month=2023-12';

interface DashboardSettingsBillingSubscriptionPlansProps {
  isSubscribed: boolean;
  isOnTrial: boolean;
  isCanceledAtPeriodEnd: boolean;
  isQuarterly?: boolean;
  isPaused: boolean;
  subscriptionPlanType: Nullable<SubscriptionTypes>;
  paymentInterval?: SubscriptionPaymentIntervalTypes;
  onUpgradeClick: (subscriptionType: SubscriptionTypes,intervalType: SubscriptionPaymentIntervalTypes) => void;
  onDowngradeToFreeClick: () => void;
  renderCurrentPlan: () => ReactElement;
  plans: SubscriptionPlanObject;
}

const STRIPE_BILLING_PORTAL_URL = 'https://billing.stripe.com/p/login/aEU2bL89lcRbc6Y288';

export const DashboardSettingsBillingSubscriptionPlans: VFC<DashboardSettingsBillingSubscriptionPlansProps> = ({
  isSubscribed,
  isPaused,
  subscriptionPlanType,
  isQuarterly,
  isOnTrial,
  isCanceledAtPeriodEnd,
  plans,
  onUpgradeClick,
  onDowngradeToFreeClick,
  paymentInterval,
  renderCurrentPlan,
}) => {
  const [activeProInterval, setActiveProInterval] = useState<SubscriptionPaymentIntervalTypes>('year');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradePlan, setUpgradePlan] = useState<SubscriptionTypes>('pro');
  const [isUpgrading, setIsUpgrading] = useState(false);

  const posthog = usePostHog()   
  const { currentUser } = useCurrentUser();
  const { currentOrganization } = useCurrentOrganization();
  const { setErrorNotification, setSuccessNotification } = useNotificationMessage();
  const location = useLocation();

  const isOnProPlan = useMemo(() => {
    return isSubscribed || isOnTrial;
  }, [isSubscribed, isOnTrial]);

  const canUpgrade = useMemo(() => {
    return !isOnProPlan || (isOnProPlan && isCanceledAtPeriodEnd);
  }, [isOnProPlan, isCanceledAtPeriodEnd]);

  const canDowngrade = useMemo(() => {
    return isOnProPlan && !isCanceledAtPeriodEnd;
  }, [isOnProPlan, isCanceledAtPeriodEnd]);

  // Check if user is on a paid plan (not trial)
  const isOnPaidPlan = useMemo(() => {
    return isSubscribed && !isOnTrial;
  }, [isSubscribed, isOnTrial]);

  // Check if user can upgrade to a specific plan
  const canUpgradeToPlan = useMemo(() => {
    return (planType: SubscriptionTypes) => {
      // If user is not on a paid plan, they can upgrade to any plan
      if (!isOnPaidPlan) {
        return true;
      }
      
      // If user is on a paid plan, they can only upgrade to higher tiers
      if (subscriptionPlanType === 'starter') {
        return planType === 'pro' || planType === 'enterprise';
      } else if (subscriptionPlanType === 'pro') {
        return planType === 'enterprise';
      } else if (subscriptionPlanType === 'enterprise') {
        return false; // Already on highest tier
      }
      
      return false;
    };
  }, [isOnPaidPlan, subscriptionPlanType]);

  const paymentIntervalText = useMemo(() => {
    if (isQuarterly) {
      return 'a quarterly Journey Grow subscription';
    } else if (paymentInterval === 'month') {
      if(subscriptionPlanType==='starter')
        return 'a monthly Journey Start subscription'
      else if(subscriptionPlanType==='pro')
        return 'a monthly Journey Grow subscription'
      else if(subscriptionPlanType==='enterprise')
        return 'a monthly Journey Scale subscription'

      return 'a monthly Journey subscription';
    }
    if(subscriptionPlanType==='starter')
      return 'an annual Journey Start subscription'
    else if(subscriptionPlanType==='pro')
      return 'an annual Journey Grow subscription'
    else if(subscriptionPlanType==='enterprise')
      return 'an annual Journey Scale subscription'
    return 'an annual Journey subscription';
  }, [paymentInterval, isQuarterly, subscriptionPlanType]);

  const currentPlanTextByPaymentInterval = useMemo(
    () => `You're currently paying for ${paymentIntervalText}`,
    [paymentIntervalText]
  );

  const onSubcribeIntervalChange = (intervalType: SubscriptionPaymentIntervalTypes) => {
    if (activeProInterval === intervalType) {
      return;
    }
    setActiveProInterval(intervalType);
  };

  const handleUpgradeClick = (planType: SubscriptionTypes) => {
    if (isOnPaidPlan) {
      // Show confirmation modal for paid plan upgrades
      setUpgradePlan(planType);
      setShowUpgradeModal(true);
    } else {
      // Direct upgrade for non-paid plans
      onUpgradeClick(planType, activeProInterval);
    }
  };

  const handleUpgradeConfirm = async () => {
    setIsUpgrading(true);
    try {
      await apiSubscribeUpgrade(currentOrganization.id, {
        method: activeProInterval,
        plan: upgradePlan
      });
      
      setSuccessNotification('Subscription upgraded successfully!');
      setShowUpgradeModal(false);
      
      // Reload the page to reflect the changes
      window.location.reload();
    } catch (error) {
      console.error('Failed to upgrade subscription:', error);
      setErrorNotification('Failed to upgrade subscription. Please try again.');
    } finally {
      setIsUpgrading(false);
    }
  };

  const getPlanDisplayName = (planType: SubscriptionTypes) => {
    switch (planType) {
      case 'starter':
        return 'Start';
      case 'pro':
        return 'Grow';
      case 'enterprise':
        return 'Scale';
      default:
        return planType;
    }
  };

  const getUpgradeButtonText = (planType: SubscriptionTypes) => {
    if (isOnPaidPlan) {
      return `Upgrade to ${getPlanDisplayName(planType)}`;
    }
    return 'Upgrade';
  };

  useEffect(()=>{

    try {
        const search = location.search;
        const params = new URLSearchParams(search);
        const successOpen = params.get('success');
        if (successOpen==='1') {
          if(currentUser && currentUser.uuid){
            posthog?.identify(currentUser.uuid, {
              userId: currentUser.uuid, ...(currentUser.name? {name: currentUser.name} : {}), subscription: 'paid',
          })
          }
          
          posthog?.capture('user_subscription_paid', {
            $set: {subscription_status: 'paid'},
          });
        
          posthog?.capture('pay_subscribed');
        }
      }
        catch (err) { 
          return;
      }

  },[])

  return (
    <div className='space-y-2 w-full'>
      <TrialStatus />
      <BillingToggle
          checked={activeProInterval==='year'}
          onChange={onSubcribeIntervalChange}
          labelType='strong'
        />

    <div className='flex space-x-4'>
      <DashboardSettingsBillingSubscriptionCard
        title='Start'
        subTitle='$9/month per user (or $89/year per user)'
        features={DashboardSettingsBillingFeatureMapping.StartPlan}
        contentContainerClassName={classNames('my-6 flex items-center gap-2', {
          'flex-col': isOnProPlan,
        })}
        renderContent={() => (
          <>
            {!isOnProPlan ? (
              <div className='flex flex-col gap-1 text-bedrock-black text-bedrock-p'>
                {plans && plans.starter && plans.starter.map((plan) => {
                  const planHasQuota = !!plan.quota;

                  if (planHasQuota) {
                    return (
                      <a
                        href={ContactUsCalendly}
                        target='_blank'
                        rel='noopener noreferrer'
                        className={classNames('cursor-pointer', {
                          'text-bedrock-p': activeProInterval !== 'quarter',
                          'text-bedrock-p-strong': activeProInterval === 'quarter',
                        })}
                        onClick={() => {
                          setActiveProInterval('quarter');
                        }}
                      >
                        {plan.label}
                      </a>
                    );
                  }

                  return (
                    <span
                      className={classNames(
                        'cursor-pointer',
                        activeProInterval === plan.payment_interval ? 'text-bedrock-p-strong' : 'opacity-80'
                      )}
                      onClick={() => {
                        onSubcribeIntervalChange(plan.payment_interval);
                      }}
                    >
                      {plan.label}
                    </span>
                  );
                })}
              </div>
            ) : 
            subscriptionPlanType==='pro' || subscriptionPlanType==='enterprise'?
            <></>
            :
            (
              <>
                <div className='mb-6'>
                  <DashboardSettingsBillingSubscriptionCurrentPlan subTitle={currentPlanTextByPaymentInterval} />
                </div>
                {renderCurrentPlan()}
              </>
            )}
          </>
        )}
        renderFooter={() => (
          <>
            {canUpgradeToPlan('starter') && (
              <div className='flex justify-center mt-auto'>
                <button
                  type='submit'
                  disabled={activeProInterval === 'quarter'}
                  className={classNames('disabled:opacity-50 btn-bedrock-primary', {
                    'pointer-events-none': activeProInterval === 'quarter',
                  })}
                  onClick={() => {
                    if (activeProInterval === 'quarter') {
                      return;
                    }
                    handleUpgradeClick('starter');
                  }}
                >
                  {getUpgradeButtonText('starter')}
                </button>
              </div>
            )}
            {canDowngrade && subscriptionPlanType==='starter' &&  (
              <div className='flex justify-center mt-auto gap-2'>
                <button
                  disabled={isPaused}
                  type='submit'
                  className='btn-bedrock-secondary disabled:opacity-50 disabled:cursor-not-allowed'
                  onClick={onDowngradeToFreeClick}
                >
                  Cancel subscription
                </button>
                <a href={STRIPE_BILLING_PORTAL_URL} target='_blank' rel='noopener noreferrer'>
                  <button className='btn-bedrock-secondary'>Update billing</button>
                </a>
              </div>
            )}
          </>
        )}
      />
      <DashboardSettingsBillingSubscriptionCard
        title='Grow'
        subTitle='$29/month per user (or $289/year per user)'
        features={DashboardSettingsBillingFeatureMapping.GrowPlan}
        contentContainerClassName={classNames('my-6 flex items-center gap-2', {
          'flex-col': isOnProPlan,
        })}
        renderContent={() => (
          <>
            {!isOnProPlan ? (
              <div className='flex flex-col gap-1 text-bedrock-black text-bedrock-p'>
                {plans && plans.pro && plans.pro.map((plan) => {
                  const planHasQuota = !!plan.quota;

                  if (planHasQuota) {
                    return (
                      <a
                        href={ContactUsCalendly}
                        target='_blank'
                        rel='noopener noreferrer'
                        className={classNames('cursor-pointer', {
                          'text-bedrock-p': activeProInterval !== 'quarter',
                          'text-bedrock-p-strong': activeProInterval === 'quarter',
                        })}
                        onClick={() => {
                          setActiveProInterval('quarter');
                        }}
                      >
                        {plan.label}
                      </a>
                    );
                  }

                  return (
                    <span
                      className={classNames(
                        'cursor-pointer',
                        activeProInterval === plan.payment_interval ? 'text-bedrock-p-strong' : 'opacity-80'
                      )}
                      onClick={() => {
                        onSubcribeIntervalChange(plan.payment_interval);
                      }}
                    >
                      {plan.label}
                    </span>
                  );
                })}
              </div>
            ) : 
            subscriptionPlanType==='starter' || subscriptionPlanType==='enterprise'?
            <></>
            :
            (
              <>
                <div className='mb-6'>
                  <DashboardSettingsBillingSubscriptionCurrentPlan subTitle={currentPlanTextByPaymentInterval} />
                </div>
                {renderCurrentPlan()}
              </>
            )}
          </>
        )}
        renderFooter={() => (
          <>
            {canUpgradeToPlan('pro') && (
              <div className='flex justify-center mt-auto'>
                <button
                  type='submit'
                  disabled={activeProInterval === 'quarter'}
                  className={classNames('disabled:opacity-50 btn-bedrock-primary', {
                    'pointer-events-none': activeProInterval === 'quarter',
                  })}
                  onClick={() => {
                    if (activeProInterval === 'quarter') {
                      return;
                    }
                    handleUpgradeClick('pro');
                  }}
                >
                  {getUpgradeButtonText('pro')}
                </button>
              </div>
            )}
            {canDowngrade && subscriptionPlanType==='pro' && (
              <div className='flex justify-center mt-auto gap-2'>
                <button
                  disabled={isPaused}
                  type='submit'
                  className='btn-bedrock-secondary disabled:opacity-50 disabled:cursor-not-allowed'
                  onClick={onDowngradeToFreeClick}
                >
                  Cancel subscription
                </button>
                <a href={STRIPE_BILLING_PORTAL_URL} target='_blank' rel='noopener noreferrer'>
                  <button className='btn-bedrock-secondary'>Update billing</button>
                </a>
              </div>
            )}
          </>
        )}
      />
      <DashboardSettingsBillingSubscriptionCard
        title='Scale'
        subTitle='$59/month per user (or $589/year per user)'
        features={DashboardSettingsBillingFeatureMapping.ScalePlan}
        contentContainerClassName={classNames('my-6 flex items-center gap-2', {
          'flex-col': isOnProPlan,
        })}
        renderContent={() => (
          <>
            {!isOnProPlan ? (
              <div className='flex flex-col gap-1 text-bedrock-black text-bedrock-p'>
                {plans && plans.enterprise && plans.enterprise.map((plan) => {
                  const planHasQuota = !!plan.quota;

                  if (planHasQuota) {
                    return (
                      <a
                        href={ContactUsCalendly}
                        target='_blank'
                        rel='noopener noreferrer'
                        className={classNames('cursor-pointer', {
                          'text-bedrock-p': activeProInterval !== 'quarter',
                          'text-bedrock-p-strong': activeProInterval === 'quarter',
                        })}
                        onClick={() => {
                          setActiveProInterval('quarter');
                        }}
                      >
                        {plan.label}
                      </a>
                    );
                  }

                  return (
                    <span
                      className={classNames(
                        'cursor-pointer',
                        activeProInterval === plan.payment_interval ? 'text-bedrock-p-strong' : 'opacity-80'
                      )}
                      onClick={() => {
                        onSubcribeIntervalChange(plan.payment_interval);
                      }}
                    >
                      {plan.label}
                    </span>
                  );
                })}
              </div>
            ) : 
            subscriptionPlanType==='starter' || subscriptionPlanType==='pro'?
            <></>
            :
            (
              <>
                <div className='mb-6'>
                  <DashboardSettingsBillingSubscriptionCurrentPlan subTitle={currentPlanTextByPaymentInterval} />
                </div>
                {renderCurrentPlan()}
              </>
            )}
          </>
        )}
        renderFooter={() => (
          <>
            {canUpgradeToPlan('enterprise') && (
              <div className='flex justify-center mt-auto'>
                <button
                  type='submit'
                  disabled={activeProInterval === 'quarter'}
                  className={classNames('disabled:opacity-50 btn-bedrock-primary', {
                    'pointer-events-none': activeProInterval === 'quarter',
                  })}
                  onClick={() => {
                    if (activeProInterval === 'quarter') {
                      return;
                    }
                    handleUpgradeClick('enterprise');
                  }}
                >
                  {getUpgradeButtonText('enterprise')}
                </button>
              </div>
            )}
            {canDowngrade && subscriptionPlanType==='enterprise' && (
              <div className='flex justify-center mt-auto gap-2'>
                <button
                  disabled={isPaused}
                  type='submit'
                  className='btn-bedrock-secondary disabled:opacity-50 disabled:cursor-not-allowed'
                  onClick={onDowngradeToFreeClick}
                >
                  Cancel subscription
                </button>
                <a href={STRIPE_BILLING_PORTAL_URL} target='_blank' rel='noopener noreferrer'>
                  <button className='btn-bedrock-secondary'>Update billing</button>
                </a>
              </div>
            )}
          </>
        )}
      />
      <DashboardSettingsBillingSubscriptionCard
        title='Teams'
        subTitle='Custom pricing for teams'
        features={DashboardSettingsBillingFeatureMapping.TeamsPlan}
        contentContainerClassName={classNames('my-6 flex items-center gap-2', {
          'flex-col': isOnProPlan,
        })}
        renderContent={() => (
          <div className='flex flex-col gap-1 text-bedrock-black text-bedrock-p'>
            <span className='text-bedrock-p'>
              Contact us for custom team pricing and features
            </span>
          </div>
        )}
        renderFooter={() => (
          <div className='flex justify-center mt-auto'>
            <a
              href={ContactUsCalendly}
              target='_blank'
              rel='noopener noreferrer'
              className='btn-bedrock-primary'
            >
              Contact us
            </a>
          </div>
        )}
      />
    </div>

    <UpgradeConfirmationModal
      isOpen={showUpgradeModal}
      onClose={() => setShowUpgradeModal(false)}
      onConfirm={handleUpgradeConfirm}
      planName={getPlanDisplayName(upgradePlan)}
      isLoading={isUpgrading}
    />

    </div>
  );
};
